import { Component, computed, DestroyRef, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { ProductsFileMgt } from '../../../../../../services/product/file-management/products.file-mgt.service';
import { SafeUrl } from '@angular/platform-browser';
import { Loading } from '../../../../../../utils/loading/loading';
import { MfgExpDatesIcon } from '../../../../../../shared/ui/icons/product-card/mfg-exp-dates-icon/mfg-exp-dates-icon';
import { BatchNumberIcon } from '../../../../../../shared/ui/icons/product-card/batch-number-icon/batch-number-icon';
import { ItemQuantityStatus } from '../item-quantity-status/item-quantity-status';
import { NormalizedNotPackageRow } from '../../../../../../pages/admin-dashboard/orders-management/view-order/view-order.model';
import { CustomerPriceIcon } from '../../../../../../shared/ui/icons/product-card/customer-price-icon/customer-price-icon';
import { BusinessPriceIcon } from '../../../../../../shared/ui/icons/product-card/business-price-icon/business-price-icon';

@Component({
  selector: 'app-order-bundle-item',
  standalone: true,
  imports: [Loading, ItemQuantityStatus, CustomerPriceIcon, BusinessPriceIcon, MfgExpDatesIcon, BatchNumberIcon],
  templateUrl: './order-bundle-item.html',
  styleUrl: './order-bundle-item.scss',
})
export class OrderBundleItem {
  private fileMgt = inject(ProductsFileMgt);
  private destroyRef = inject(DestroyRef);
  private locale = inject(LOCALE_ID);
  isArabic = computed(() => this.locale.startsWith('ar'));
  productTitle = input();

  totalBusinessPrice = input();
  totalPeoplePrice = input<number>();
  quantity = input<string | number>('');

  profileImageFid = input<string>();
  isLoadingFile = signal(false);
  previewImageUrl = signal<SafeUrl | string>('/assets/product.png');

  products = input<any[]>();
  totalBusinessPriceBeforeDiscount = input();
  bundleDiscountType = input<string>();
  bundleDiscountValue = input<string>();

  isPrinted = input<boolean>(false);
  isReadOnly = input<boolean>(false);
  showOnlyPackaged = input<boolean>(false);
  businessPrice = input();
  packageQuantity = input<string | null>(null);
  notPackageRows = input<NormalizedNotPackageRow[] | null>(null);
  receiveReturnedProductsView = input<boolean>(false);

  productPackageClicked = output<any>();

  isPartiallyPackaged = input<boolean>(false);
  confirmAction = input<string | null>(null);

  packageState = input<Record<string, { packageQuantity: string | null; notPackageRows: any[] }>>({});
  bundleItemId = input<string | number>();

  convertClicked = output<void>();
  dropAllClicked = output<void>();

  bundleNotPackageRows = computed<any[]>(() => {
    const rows = (this.products() ?? []).flatMap((row: any) => this.getPackageDataFor(row).notPackageRows ?? []);
    if (rows.length > 0) return rows;
    return this.notPackageRows() ?? [];
  });

  showShortageActions = computed(
    () =>
      !this.isReadOnly() &&
      this.isPartiallyPackaged() &&
      (this.bundleNotPackageRows()?.length ?? 0) > 0 &&
      !this.confirmAction(),
  );

  isFullyUnpackaged = computed(() => {
    const packed = this.packageQuantity();
    const hasPackaged = packed !== null && Number(packed) > 0;
    if (hasPackaged) return false;
    const rows = this.notPackageRows() ?? [];
    if (rows.length === 0) return false;
    const notPacked = rows.reduce((acc, r) => acc + (+r.quantity || 0), 0);
    return notPacked >= +this.quantity();
  });

  totalPeoplePriceComputed = computed(() => {
    const directTotal = +(this.totalPeoplePrice() || 0);
    if (directTotal > 0) return directTotal;
    return (this.products() ?? []).reduce((sum, p) => sum + +(p?.product?.peoplePrice || 0) * +(p?.quantity || 0), 0);
  });

  ngOnChanges() {
    const fid = this.profileImageFid();
    if (!fid) return;

    this.isLoadingFile.set(true);

    const subscription = this.fileMgt.getImageUrl(fid).subscribe({
      next: (res) => {
        this.isLoadingFile.set(false);

        if (res.isImage) {
          this.previewImageUrl.set(res.url);
        } else {
          window.open(res.url as string, '_blank');
        }
      },
      error: () => {
        this.isLoadingFile.set(false);
        this.previewImageUrl.set('/assets/product.png');
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  getPackageDataFor(row: any) {
    const storeEntry = this.getStoreEntryFor(row);
    if (storeEntry) {
      return {
        packageQuantity: storeEntry.packageQuantity ?? null,
        notPackageRows: storeEntry.notPackageRows ?? [],
      };
    }

    const product = row?.product ?? row;
    const history = row?.packagingHistory ?? product?.packagingHistory;
    const hasHistory = history && (history.packaged !== undefined || history.notPackaged !== undefined);
    const packaged = hasHistory ? history.packaged : product?.package;
    const notPackage = hasHistory ? history.notPackaged : product?.notPackage;

    const packageQuantity = packaged?.quantity != null ? String(packaged.quantity) : null;

    const notPackageRows = Array.isArray(notPackage)
      ? notPackage.map((r: any) => {
          const reasonWrapper =
            r.reason != null
              ? { reason: r.reason, otherReason: r.otherReason }
              : (r.productReason ?? r.bundleReason ?? null);
          const reason = reasonWrapper?.reason ?? r;
          return {
            quantity: r?.quantity != null ? String(r.quantity) : '0',
            reasonId: reason?.id,
            reasonTitleEn: reason?.titleEn,
            reasonTitleAr: reason?.titleAr,
            otherReason: reasonWrapper?.otherReason ?? undefined,
          };
        })
      : [];

    return { packageQuantity, notPackageRows };
  }

  private getStoreEntryFor(row: any): { packageQuantity: string | null; notPackageRows: any[] } | undefined {
    const state = this.packageState();
    if (!state) return undefined;

    const rowId = row?.id;
    if (rowId == null) return undefined;

    if (this.bundleItemId() != null) {
      const scoped = state[`${this.bundleItemId()}_${rowId}`];
      if (scoped) return scoped;
    }

    return state[rowId];
  }

  bundleTotal = computed<number>(() => {
    const inputVal = +(this.totalBusinessPrice() || 0);
    if (inputVal > 0) return inputVal;
    return (this.products() ?? []).reduce((sum, p) => sum + +(p?.product?.businessPrice || 0) * +(p?.quantity || 0), 0);
  });

  displayPharmacyPrice = computed(() => {
    const unit = parseFloat(String(this.businessPrice() ?? '0'));
    let qty = 0;
    if (this.showOnlyPackaged()) {
      qty = +(this.packageQuantity() || '0');
    } else {
      const packaged = +(this.packageQuantity() || '0');
      const unpackaged = (this.notPackageRows() ?? []).reduce((sum, r) => sum + +(r?.quantity || 0), 0);
      qty = packaged + unpackaged;
    }
    const calc = unit * qty;
    if (calc > 0) return +calc.toFixed(2);
    return this.bundleTotal();
  });

  concatenationDate(mfgDate: string, expDate: string) {
    const date = new Date(expDate);
    const shortYear = date.getFullYear().toString().slice(-2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const expDateYear = month + ' - ' + shortYear;

    const mfgDateVar = new Date(mfgDate);
    const mfgDateYear = mfgDateVar.getFullYear().toString().slice(-2);

    return mfgDateYear + ' - ' + expDateYear;
  }

  onOpenProductPackageModal(row: any) {
    if (this.isReadOnly()) return;
    this.productPackageClicked.emit(row);
  }

  onConvertClick() {
    if (this.isReadOnly()) return;
    this.convertClicked.emit();
  }

  onDropAllClick() {
    if (this.isReadOnly()) return;
    this.dropAllClicked.emit();
  }
}
